import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Calendar as CalendarIcon, Plus, Download, Trash2, Edit,
  Stethoscope, Package, Syringe, AlertCircle, Bell, Check, X,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const B = {
  border: `1px solid ${C.navy}15`,
  shadow: "none",
  shadowSm: "4px 4px 0px #2D3561",
  font: BODY,
  mono: "'IBM Plex Mono', monospace",
};

// Configure moment locale to Spanish
moment.locale("es", {
  months: "Enero_Febrero_Marzo_Abril_Mayo_Junio_Julio_Agosto_Septiembre_Octubre_Noviembre_Diciembre".split("_"),
  monthsShort: "Ene_Feb_Mar_Abr_May_Jun_Jul_Ago_Sep_Oct_Nov_Dic".split("_"),
  weekdays: "Domingo_Lunes_Martes_Miércoles_Jueves_Viernes_Sábado".split("_"),
  weekdaysShort: "Dom_Lun_Mar_Mié_Jue_Vie_Sáb".split("_"),
  weekdaysMin: "Do_Lu_Ma_Mi_Ju_Vi_Sá".split("_"),
  week: { dow: 1 }, // Monday is the first day of the week
});

const localizer = momentLocalizer(moment);

// Event types with colors and icons
const EVENT_TYPES = {
  veterinario: { 
    label: "Cita Veterinaria", 
    color: "#3b82f6", 
    bgColor: "#dbeafe",
    icon: Stethoscope 
  },
  vacuna: { 
    label: "Vacunación", 
    color: "#8b5cf6", 
    bgColor: "#ede9fe",
    icon: Syringe 
  },
  suministros: { 
    label: "Revisar Suministros", 
    color: "#f59e0b", 
    bgColor: "#fef3c7",
    icon: Package 
  },
  medicacion: { 
    label: "Medicación", 
    color: "#ef4444", 
    bgColor: "#fee2e2",
    icon: AlertCircle 
  },
  recordatorio: { 
    label: "Recordatorio", 
    color: "#22c55e", 
    bgColor: "#dcfce7",
    icon: Bell 
  },
  otro: { 
    label: "Otro", 
    color: "#64748b", 
    bgColor: "#f1f5f9",
    icon: CalendarIcon 
  },
};

// Custom toolbar with Spanish labels
const CustomToolbar = ({ label, onNavigate, onView, view }) => {
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4 p-4 bg-white rounded-xl border border-slate-200">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigate("PREV")}
          className="h-9"
        >
          &lt;
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigate("TODAY")}
          className="h-9"
        >
          Hoy
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigate("NEXT")}
          className="h-9"
        >
          &gt;
        </Button>
      </div>
      
      <h2 className="font-heading text-lg font-bold text-[var(--navy)] capitalize">
        {label}
      </h2>
      
      <div className="flex items-center gap-2">
        {["month", "week", "day", "agenda"].map((v) => (
          <Button
            key={v}
            variant={view === v ? "default" : "outline"}
            size="sm"
            onClick={() => onView(v)}
            className={view === v ? "bg-[#8B9A2D] hover:bg-[#6a6916]" : ""}
          >
            {v === "month" ? "Mes" : v === "week" ? "Semana" : v === "day" ? "Día" : "Agenda"}
          </Button>
        ))}
      </div>
    </div>
  );
};

// Custom event component
const EventComponent = ({ event }) => {
  const eventType = EVENT_TYPES[event.type] || EVENT_TYPES.otro;
  const Icon = eventType.icon;
  
  return (
    <div 
      className="flex items-center gap-1 px-1 py-0.5 rounded text-xs font-medium truncate"
      style={{ 
        backgroundColor: eventType.bgColor,
        color: eventType.color,
        borderLeft: `3px solid ${eventType.color}`
      }}
    >
      <Icon className="w-3 h-3 flex-shrink-0" />
      <span className="truncate">{event.title}</span>
    </div>
  );
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());
  
  // Dialog states
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    type: "recordatorio",
    start: "",
    end: "",
    description: "",
    animal_name: "",
    all_day: false,
    reminder_24h: true,
    reminder_1h: true,
  });
  
  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState({
    weekly_summary: false,
    supply_expiry_alerts: true,
  });
  const [showPrefsDialog, setShowPrefsDialog] = useState(false);

  const loadEvents = useCallback(async () => {
    try {
      const token = localStorage.getItem("ea_token");
      const [eventsRes, prefsRes] = await Promise.all([
        axios.get(`${API}/prepara/calendar`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/prepara/notification-preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: {} })),
      ]);
      
      // Convert dates for react-big-calendar
      const formattedEvents = (eventsRes.data || []).map(event => ({
        ...event,
        start: new Date(event.start),
        end: new Date(event.end),
      }));
      setEvents(formattedEvents);
      
      if (prefsRes.data) {
        setNotifPrefs(prefsRes.data);
      }
    } catch (error) {
      console.error("Error loading events:", error);
      toast.error("Error al cargar los eventos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    loadEvents();
  }, [user, loadEvents, navigate]);

  const handleSelectSlot = useCallback(({ start, end }) => {
    setSelectedEvent(null);
    setIsEditing(false);
    setFormData({
      title: "",
      type: "recordatorio",
      start: moment(start).format("YYYY-MM-DDTHH:mm"),
      end: moment(end).format("YYYY-MM-DDTHH:mm"),
      description: "",
      animal_name: "",
      all_day: false,
      reminder_24h: true,
      reminder_1h: true,
    });
    setShowEventDialog(true);
  }, []);

  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event);
    setIsEditing(true);
    setFormData({
      title: event.title,
      type: event.type,
      start: moment(event.start).format("YYYY-MM-DDTHH:mm"),
      end: moment(event.end).format("YYYY-MM-DDTHH:mm"),
      description: event.description || "",
      animal_name: event.animal_name || "",
      all_day: event.all_day || false,
      reminder_24h: event.reminder_24h !== false,
      reminder_1h: event.reminder_1h !== false,
    });
    setShowEventDialog(true);
  }, []);
  
  const handleSaveNotifPrefs = async () => {
    try {
      const token = localStorage.getItem("ea_token");
      await axios.put(
        `${API}/prepara/notification-preferences`,
        notifPrefs,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Preferencias guardadas");
      setShowPrefsDialog(false);
    } catch (error) {
      console.error("Error saving preferences:", error);
      toast.error("Error al guardar preferencias");
    }
  };

  const handleSaveEvent = async () => {
    if (!formData.title.trim()) {
      toast.error("El título es obligatorio");
      return;
    }

    try {
      const token = localStorage.getItem("ea_token");
      const eventData = {
        ...formData,
        start: new Date(formData.start).toISOString(),
        end: new Date(formData.end).toISOString(),
      };

      if (isEditing && selectedEvent) {
        await axios.put(
          `${API}/prepara/calendar/${selectedEvent.event_id}`,
          eventData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Evento actualizado");
      } else {
        await axios.post(
          `${API}/prepara/calendar`,
          eventData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Evento creado");
      }

      setShowEventDialog(false);
      loadEvents();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error("Error al guardar el evento");
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      const token = localStorage.getItem("ea_token");
      await axios.delete(
        `${API}/prepara/calendar/${selectedEvent.event_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Evento eliminado");
      setShowDeleteDialog(false);
      setShowEventDialog(false);
      loadEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Error al eliminar el evento");
    }
  };

  // Export events to ICS format
  const exportToICS = () => {
    if (events.length === 0) {
      toast.error("No hay eventos para exportar");
      return;
    }

    let icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Emergencia Animal//Prepara//ES",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "X-WR-CALNAME:Prepara - Calendario de Emergencias",
    ];

    events.forEach(event => {
      const startDate = moment(event.start).format("YYYYMMDDTHHmmss");
      const endDate = moment(event.end).format("YYYYMMDDTHHmmss");
      const uid = `${event.event_id}@emergenciaanimal.org`;
      const eventType = EVENT_TYPES[event.type]?.label || "Evento";

      icsContent.push(
        "BEGIN:VEVENT",
        `UID:${uid}`,
        `DTSTART:${startDate}`,
        `DTEND:${endDate}`,
        `SUMMARY:${event.title}`,
        `DESCRIPTION:${event.description || ""} [${eventType}]${event.animal_name ? ` - Animal: ${event.animal_name}` : ""}`,
        `CATEGORIES:${eventType}`,
        "END:VEVENT"
      );
    });

    icsContent.push("END:VCALENDAR");

    const blob = new Blob([icsContent.join("\r\n")], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `prepara_calendario_${moment().format("YYYY-MM-DD")}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success("Calendario exportado a .ics");
  };

  // Custom messages in Spanish
  const messages = {
    today: "Hoy",
    previous: "Anterior",
    next: "Siguiente",
    month: "Mes",
    week: "Semana",
    day: "Día",
    agenda: "Agenda",
    date: "Fecha",
    time: "Hora",
    event: "Evento",
    noEventsInRange: "No hay eventos en este rango",
    showMore: (total) => `+ ${total} más`,
  };

  // Event style getter
  const eventStyleGetter = (event) => {
    const eventType = EVENT_TYPES[event.type] || EVENT_TYPES.otro;
    return {
      style: {
        backgroundColor: eventType.bgColor,
        color: eventType.color,
        borderLeft: `3px solid ${eventType.color}`,
        borderRadius: "4px",
        padding: "2px 4px",
        fontSize: "12px",
      },
    };
  };

  if (!user) return null;

  return (
    <div className="min-h-screen" data-testid="calendar-page" style={{ background: "#f8f7f4", fontFamily: B.font }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: "#2D3561", borderBottom: B.border }}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/prepara/panel")}
                className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:opacity-80"
                style={{ fontFamily: B.mono, color: "#f8f7f4" }}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                PANEL
              </button>
              <div className="h-5 w-px" style={{ background: "#f8f7f440" }} />
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 flex items-center justify-center" style={{ background: "#8B9A2D", border: "2px solid #f8f7f4" }}>
                  <CalendarIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-base font-bold" style={{ color: "#f8f7f4" }}>CALENDARIO</h1>
                  <p className="text-[9px] tracking-widest" style={{ fontFamily: B.mono, color: "#8B9A2D" }}>RECORDATORIOS Y CITAS</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPrefsDialog(true)}
                className="hidden sm:flex w-8 h-8 items-center justify-center hover:opacity-80"
                style={{ border: "2px solid #8B9A2D" }}
                data-testid="notif-prefs-btn"
                title="Configurar notificaciones"
              >
                <Bell className="w-4 h-4" style={{ color: "#8B9A2D" }} />
              </button>
              <button
                onClick={exportToICS}
                className="hidden sm:flex px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider items-center gap-1.5 hover:-translate-y-0.5 transition-all"
                style={{ fontFamily: B.mono, color: "#8B9A2D", border: "2px solid #8B9A2D" }}
                data-testid="export-ics-btn"
              >
                <Download className="w-3.5 h-3.5" />
                EXPORTAR .ICS
              </button>
              <button
                onClick={() => {
                  setSelectedEvent(null);
                  setIsEditing(false);
                  setFormData({
                    title: "",
                    type: "recordatorio",
                    start: moment().format("YYYY-MM-DDTHH:mm"),
                    end: moment().add(1, "hour").format("YYYY-MM-DDTHH:mm"),
                    description: "",
                    animal_name: "",
                    all_day: false,
                    reminder_24h: true,
                    reminder_1h: true,
                  });
                  setShowEventDialog(true);
                }}
                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 hover:-translate-y-0.5 transition-all"
                style={{ fontFamily: B.mono, background: "#8B9A2D", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
                data-testid="new-event-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                NUEVO EVENTO
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Event Type Legend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 items-center">
                <span className="text-sm font-medium text-slate-600">Tipos de evento:</span>
                {Object.entries(EVENT_TYPES).map(([key, type]) => {
                  const Icon = type.icon;
                  return (
                    <Badge
                      key={key}
                      variant="secondary"
                      className="flex items-center gap-1.5 px-2 py-1"
                      style={{ 
                        backgroundColor: type.bgColor,
                        color: type.color,
                        border: `1px solid ${type.color}30`
                      }}
                    >
                      <Icon className="w-3 h-3" />
                      {type.label}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Calendar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="overflow-hidden">
            <CardContent className="p-4">
              {loading ? (
                <div className="h-[600px] flex items-center justify-center">
                  <div className="w-10 h-10 border-4 border-[#8B9A2D] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="calendar-container" style={{ height: "600px" }}>
                  <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: "100%" }}
                    view={view}
                    onView={setView}
                    date={date}
                    onNavigate={setDate}
                    selectable
                    onSelectSlot={handleSelectSlot}
                    onSelectEvent={handleSelectEvent}
                    components={{
                      toolbar: CustomToolbar,
                      event: EventComponent,
                    }}
                    messages={messages}
                    eventPropGetter={eventStyleGetter}
                    popup
                    views={["month", "week", "day", "agenda"]}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { label: "Total eventos", value: events.length, color: "#8B9A2D" },
            { 
              label: "Esta semana", 
              value: events.filter(e => moment(e.start).isSame(moment(), "week")).length,
              color: "#3b82f6" 
            },
            { 
              label: "Próximos 7 días", 
              value: events.filter(e => moment(e.start).isBetween(moment(), moment().add(7, "days"))).length,
              color: "#22c55e" 
            },
            { 
              label: "Citas veterinarias", 
              value: events.filter(e => e.type === "veterinario").length,
              color: "#8b5cf6" 
            },
          ].map((stat, idx) => (
            <Card key={idx}>
              <CardContent className="p-4 text-center">
                <p className="font-heading text-2xl font-bold" style={{ color: stat.color }}>
                  {stat.value}
                </p>
                <p className="font-body text-xs text-slate-500">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </motion.div>

        {/* Upcoming Events List */}
        {events.filter(e => moment(e.start).isAfter(moment())).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-6"
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#8B9A2D]" />
                  Próximos eventos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {events
                    .filter(e => moment(e.start).isAfter(moment()))
                    .sort((a, b) => new Date(a.start) - new Date(b.start))
                    .slice(0, 5)
                    .map((event) => {
                      const eventType = EVENT_TYPES[event.type] || EVENT_TYPES.otro;
                      const Icon = eventType.icon;
                      return (
                        <div
                          key={event.event_id}
                          className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-[#8B9A2D]/30 transition-colors cursor-pointer"
                          onClick={() => handleSelectEvent(event)}
                        >
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: eventType.bgColor }}
                          >
                            <Icon className="w-5 h-5" style={{ color: eventType.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-[var(--navy)] truncate">{event.title}</h4>
                            <p className="text-sm text-slate-500">
                              {moment(event.start).format("DD MMM YYYY, HH:mm")}
                              {event.animal_name && ` • ${event.animal_name}`}
                            </p>
                          </div>
                          <Badge
                            variant="secondary"
                            style={{ backgroundColor: eventType.bgColor, color: eventType.color }}
                          >
                            {eventType.label}
                          </Badge>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-md" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-[#8B9A2D]" />
              {isEditing ? "Editar evento" : "Nuevo evento"}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? "Modifica los detalles del evento" : "Añade un nuevo evento a tu calendario"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ej: Vacuna anual de Max"
                data-testid="event-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Tipo de evento</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger data-testid="event-type-select">
                  <SelectValue placeholder="Selecciona un tipo" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(EVENT_TYPES).map(([key, type]) => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" style={{ color: type.color }} />
                          {type.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="start">Inicio</Label>
                <Input
                  id="start"
                  type="datetime-local"
                  value={formData.start}
                  onChange={(e) => setFormData({ ...formData, start: e.target.value })}
                  data-testid="event-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end">Fin</Label>
                <Input
                  id="end"
                  type="datetime-local"
                  value={formData.end}
                  onChange={(e) => setFormData({ ...formData, end: e.target.value })}
                  data-testid="event-end-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="animal_name">Nombre del animal (opcional)</Label>
              <Input
                id="animal_name"
                value={formData.animal_name}
                onChange={(e) => setFormData({ ...formData, animal_name: e.target.value })}
                placeholder="Ej: Max, Luna, etc."
                data-testid="event-animal-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción (opcional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Notas adicionales..."
                rows={3}
                data-testid="event-description-input"
              />
            </div>
            
            {/* Reminder Settings */}
            <div className="space-y-3 pt-2 border-t border-slate-100">
              <Label className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#8B9A2D]" />
                Recordatorios
              </Label>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.reminder_24h}
                    onChange={(e) => setFormData({ ...formData, reminder_24h: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-[#8B9A2D] focus:ring-[#8B9A2D]"
                  />
                  <span className="text-sm text-slate-600">24 horas antes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.reminder_1h}
                    onChange={(e) => setFormData({ ...formData, reminder_1h: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-[#8B9A2D] focus:ring-[#8B9A2D]"
                  />
                  <span className="text-sm text-slate-600">1 hora antes</span>
                </label>
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <div>
              {isEditing && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                  data-testid="delete-event-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Eliminar
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEventDialog(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSaveEvent}
                className="bg-[#8B9A2D] hover:bg-[#6a6916]"
                data-testid="save-event-btn"
              >
                <Check className="w-4 h-4 mr-1" />
                {isEditing ? "Guardar cambios" : "Crear evento"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Eliminar evento
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que quieres eliminar "{selectedEvent?.title}"? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteEvent}
              data-testid="confirm-delete-btn"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Preferences Dialog */}
      <Dialog open={showPrefsDialog} onOpenChange={setShowPrefsDialog}>
        <DialogContent className="max-w-md" data-testid="notif-prefs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#8B9A2D]" />
              Preferencias de Notificaciones
            </DialogTitle>
            <DialogDescription>
              Configura qué notificaciones quieres recibir
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Weekly Summary */}
            <div className="flex items-start gap-4 p-4 rounded-lg bg-slate-50">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <CalendarIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-[var(--navy)]">Resumen semanal</h4>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifPrefs.weekly_summary}
                      onChange={(e) => setNotifPrefs({ ...notifPrefs, weekly_summary: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#8B9A2D]/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#8B9A2D]"></div>
                  </label>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Recibe un resumen de tus eventos cada lunes por la mañana
                </p>
              </div>
            </div>

            {/* Supply Expiry Alerts */}
            <div className="flex items-start gap-4 p-4 rounded-lg bg-slate-50">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-[var(--navy)]">Caducidad de suministros</h4>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifPrefs.supply_expiry_alerts}
                      onChange={(e) => setNotifPrefs({ ...notifPrefs, supply_expiry_alerts: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#8B9A2D]/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#8B9A2D]"></div>
                  </label>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Recibe alertas cuando los items de tu kit estén por caducar (7 días antes)
                </p>
              </div>
            </div>

            {/* Info Box */}
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-sm text-blue-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Los recordatorios de eventos (24h y 1h antes) se configuran individualmente al crear o editar cada evento.
                </span>
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrefsDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveNotifPrefs}
              className="bg-[#8B9A2D] hover:bg-[#6a6916]"
            >
              <Check className="w-4 h-4 mr-1" />
              Guardar preferencias
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Calendar Styles */}
      <style>{`
        .rbc-calendar {
          font-family: inherit;
        }
        .rbc-header {
          padding: 8px;
          font-weight: 600;
          color: var(--navy);
          background: #f8fafc;
          border-bottom: 2px solid #e2e8f0;
        }
        .rbc-today {
          background-color: #8B9A2D10 !important;
        }
        .rbc-off-range-bg {
          background-color: #f8fafc;
        }
        .rbc-event {
          border: none !important;
          background-color: transparent !important;
        }
        .rbc-event-content {
          overflow: hidden;
        }
        .rbc-selected {
          background-color: #8B9A2D20 !important;
        }
        .rbc-show-more {
          color: #8B9A2D;
          font-weight: 500;
        }
        .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
          padding: 8px 12px;
          border-bottom: 1px solid #e2e8f0;
        }
        .rbc-agenda-event-cell {
          overflow: visible !important;
        }
        .rbc-time-header-content {
          border-left: 1px solid #e2e8f0;
        }
        .rbc-timeslot-group {
          min-height: 60px;
        }
        .rbc-current-time-indicator {
          background-color: #ef4444;
          height: 2px;
        }
      `}</style>
    </div>
  );
}
